"use client";
import {useState, useCallback} from "react";
import styles from './index.module.scss';
import IconButton from "@/app/_components/Shared/IconButton";
import PersonalDetails from "@/app/_components/BankingSteps/PersonalDetails";
import BusinessDetails from "@/app/_components/BankingSteps/BusinessDetails";
import {useRouter} from "nextjs-toploader/app";
import axios from "axios";
import {toast} from "react-toastify";
import {object, string, array} from "yup";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Calendar from "@/app/_components/BankingSteps/Calendar";
import {calculateRiskScore} from "@/app/_components/BankingSteps/calculate";
import Complete from "@/app/_components/BankingSteps/Complete";
import { sendGTMEvent } from '@next/third-parties/google'

const steps = [
    "Basic Information",
    "Company & Ownership Details",
    "Schedule Your Free Consultation",
    "Calculate Your Risk Score",
];

const stepsSchema = {
    1: {
        full_name: string().required(),
        business_name: string(),
        email: string().email().required(),
        phone: string() .test('is-valid-international-phone', 'Invalid phone number', value => {
            if (!value) return false;
            const phone = parsePhoneNumberFromString(value);
            return phone ? phone.isValid() : false;
        }).required(),
    },
    2: {
        uae_visa: string().required(),
        nationalities: array().min(1).required(),
        business_activity: object().shape({
            value: string().required(),
            label: string().required(),
        }).required(),
        annual_turnover: object().shape({
            value: string().required(),
            label: string().required(),
        }).required(),
        deposit_amount: object().shape({
            value: string().required(),
            label: string().required(),
        }).required(),
        countries: array().min(1).required(),
    },
    3: {}
}
// merge all schemas into one
const schema = object().shape(Object.assign({}, ...Object.values(stepsSchema)));

export default function BankingSteps() {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [data, setData] = useState({
        full_name: '',
        business_name: '',
        email: '',
        phone: '',
        uae_visa: 'Yes',
        nationalities: [],
        location: '',
        jurisdiction: { value: "Mainland", label: "Mainland" },
        business_activity: null,
        annual_turnover: null,
        deposit_amount: null,
        countries: [],
        calendly_event: null,
        calendly_invitee: null,
    });
    const [score, setScore] = useState(0);

    const handleNext = useCallback(() => {
        const schema = object().shape(stepsSchema[step]);
        schema.validate(data).then(() => {
            setStep(step + 1);
            axios.post( process.env.NEXT_PUBLIC_API_URL + '/tickets', {
                name: data.full_name,
                type: 'banking',
                ...data,
                message: 'Type: Banking\n',
                data: data
            })
        }).catch(error => {
            toast.error(error.message);
        });
        try {
            sendGTMEvent({ event: 'banking_event', value: JSON.stringify(data) });
        } catch (e) {
            console.error(e);
        }
    }, [data, step]);

    const handleBack = useCallback(() => {
        setStep(step - 1);
    }, [step]);

    const handleChange = useCallback((key, value) => {
        setData({...data, [key]: value});
    }, [data]);

    const submit = useCallback(async () => {
        setIsLoading(true);
        const customData = {
            ...data,
            jurisdiction: data.jurisdiction.value,
            nationalities: data.nationalities.map(nat => nat.value),
            business_activity: data.business_activity.value,
            annual_turnover: data.annual_turnover.value,
            deposit_amount: data.deposit_amount.value,
            countries: data.countries.map(country => country.value),
        };
        const score = calculateRiskScore(customData);
        console.log("Risk Score", score);
        customData.score = score;
        setScore(score);
        schema.validate(data).then(() => {
            let message = 'Type: Banking\n';
            message += 'Full Name: ' + customData.full_name + '\n';
            message += 'Email: ' + customData.email + '\n';
            message += 'Phone: ' + customData.phone + '\n';
            message += 'UAE Visa: ' + customData.uae_visa + '\n';
            message += 'Nationalities: ' + customData.nationalities.join(', ') + '\n';
            message += 'Location: ' + customData.location + '\n';
            message += 'Jurisdiction: ' + customData.jurisdiction + '\n';
            message += 'Business Activity: ' + customData.business_activity + '\n';
            message += 'Annual Turnover: ' + customData.annual_turnover + '\n';
            message += 'Deposit Amount: ' + customData.deposit_amount + '\n';
            message += 'Countries: ' + customData.countries.join(', ') + '\n';
            message += 'Calendly Event: ' + customData.calendly_event + '\n';
            message += 'Calendly Invitee: ' + customData.calendly_invitee + '\n';
            message += 'Risk Score: ' + customData.score + '\n';
            return axios.post( process.env.NEXT_PUBLIC_API_URL + '/tickets', {
                name: customData.full_name,
                type: 'banking',
                ...customData,
                message,
                data: customData
            }).then(response => {
                handleNext();
            }).catch(error => {
                toast.error('Failed to send message. Please try again later');
            });
        }).catch(error => {
            toast.error(error.message);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [data, handleNext])

    return (
        <div className="container-wrapper">
            <div className={styles.container}>
                <div className="container-wrapper">
                    <div className={styles.sidebar}>
                        <div className="container-wrapper">
                            {steps.map((stepName, index) => (
                                <div key={index} className={`${styles.step} ${step === index + 1 ? styles.active : ''}`}>
                                    <span>{index + 1}</span>
                                    {stepName}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={styles.form}>
                        <div className="container-wrapper">
                            {
                                step === 1 && <PersonalDetails data={data} onChange={handleChange} />
                            }
                            {
                                step === 2 && <BusinessDetails data={data} onChange={handleChange} />
                            }
                            {
                                step === 3 && <Calendar data={data} onChange={handleChange} submit={submit} />
                            }
                            {
                                step === 4 && <Complete data={data} score={score} />
                            }
                            <div className={styles.buttons}>
                                {
                                    (step > 1 && step < 4) && <IconButton color="secondary" onClick={handleBack}>Previous</IconButton>
                                }
                                {
                                    (step < 3) && <IconButton onClick={handleNext}>Next</IconButton>
                                }
                                {
                                    (step === 4) && <IconButton onClick={() => null}>{"We're here if you need anything."}</IconButton>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
